import type { EcritureComptable } from "../../lib/comptable";
import TableauComptable, { TABLE_STYLES } from "./TableauComptable";

export const fmtDate = (d: string) =>
  d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "—";

const DEFAULT_PLACEHOLDER_ROWS = [
  { c: "6xx", l: "Charge" },
  { c: "445660", l: "TVA déductible" },
  { c: "4011", l: "Fournisseurs" },
];

export interface BlocEcritureProps {
  titre: string;
  ecriture: EcritureComptable | null;
  emptyMessage: string;
  emptySubtext?: string;
  placeholderRows?: { c: string; l: string }[];
  /** Affiche « Modifier » à côté du titre si l’écriture est en brouillon. */
  onEditDraft?: () => void;
  /** Tableau d’écritures plus dense (drawer factures fournisseurs compta). */
  compactTable?: boolean;
}

export default function BlocEcriture({
  titre,
  ecriture,
  emptyMessage,
  emptySubtext,
  placeholderRows = DEFAULT_PLACEHOLDER_ROWS,
  onEditDraft,
  compactTable = false,
}: BlocEcritureProps) {
  const lignesNormalisees =
    ecriture?.lignes.map((l) => ({
      ...l,
      debit: Math.round(Number(l.debit) || 0),
      credit: Math.round(Number(l.credit) || 0),
    })) ?? undefined;
  const totalDebit = lignesNormalisees?.reduce((s, l) => s + l.debit, 0) ?? 0;
  const totalCredit = lignesNormalisees?.reduce((s, l) => s + l.credit, 0) ?? 0;
  return (
    <div
      style={{
        background: "white",
        border: "1px solid #e2e8f0",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          padding: compactTable ? "10px 14px" : "16px 20px",
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: compactTable ? 8 : 10, flexWrap: "wrap" }}>
          {ecriture && (
            <span
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.06em",
                padding: "4px 10px",
                borderRadius: 8,
                background: "#f8fafc",
                color: "#475569",
                border: "1px solid #e2e8f0",
              }}
            >
              {ecriture.journal_code}
            </span>
          )}
          <span
            style={{
              fontSize: ecriture ? (compactTable ? 13 : 15) : compactTable ? 12 : 13,
              fontWeight: 600,
              color: ecriture ? "#0f172a" : "#64748b",
              flex: 1,
            }}
          >
            {ecriture ? ecriture.libelle : titre}
          </span>
          {ecriture && (
            <span style={{ fontSize: compactTable ? 11 : 12, color: "#94a3b8", fontWeight: 500 }}>
              {fmtDate(ecriture.date_ecriture)}
            </span>
          )}
          {ecriture?.statut === "draft" && onEditDraft && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditDraft();
              }}
              style={{
                marginLeft: "auto",
                padding: "6px 12px",
                borderRadius: 8,
                border: "1px solid #fed7aa",
                background: "#fff7ed",
                color: "#c2410c",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              }}
            >
              Modifier
            </button>
          )}
        </div>
      </div>
      <TableauComptable
        lignes={lignesNormalisees}
        totalDebit={totalDebit}
        totalCredit={totalCredit}
        empty={!ecriture}
        emptyMessage={emptyMessage}
        emptySubtext={emptySubtext}
        subheader={undefined}
        embedded
        compact={compactTable}
        placeholderRows={placeholderRows}
      />
    </div>
  );
}

export { TABLE_STYLES };
